// 個人基本資料維護整頁（profile-profile-maintenance＝legacy profile/profileMaintenance.jsp）。
// 批次 16（B8-2，Gitea issue #15）。掛在 PageBehavior.complexBody（批次 13 hook 先例）；
// 本頁 pageType=crud-list 是產生檔紅線不可改，CrudListPage 補 complexBody 早退（批次 16 擴充）
// ——泛用「查詢＋列表」模板與本頁「單筆表單＋證照子清單」形狀不匹配（KNOWN-NOT-DONE B8-2：
// 與帳號大 modal 同型專批，B1-2 AccountV2DialogModal 為參考實作）。
//
// legacy 流程（行號全部親驗 2026-09-25，基準＝ePortfolio_work/ePortfolio-tsgh/WebContent/；
// 詳 docs/plans/2026-09-25-batch16-profile-plan.md §1）：
//   - 表單（profileMaintenance.jsp:108-285）：21 契約欄位（name 照契約）＋員工編號/學號UGY
//     （無 name，:136/:142）；0001855 不可維護欄位 readonly（ProfileController.java:96-98，
//     後端 :102/:107-108/:115-116 全被註解）；App簡碼 id 重覆 quirk（:165-171）不重現。
//   - save()（:42-96）：pin_again≠loginPin→inline 紅字 密碼不符合!（:52）＋alertMessage；
//     itemForm.valid()；FormData POST profile/saveMyAccountAjax（:64）→成功 showInfoDialog(desc)
//     （:71）＋照片刷新（:76-84）；alertMessage≠""→showInfoDialog 且不送出（:90-93）。
//   - 證照（profileMaintenanceTab_certification.jsp）：進場即載（main.jsp:21 觸發 tab click）；
//     showList（:42-64）POST config/certification/getCertificationListAjax＋type select 重填
//     （:50-56）；表 5 欄（:198-202，col0 刪除鈕 erasable==1、col1 名稱連結、col4「其他」＝type）；
//     showEdit（:66-110） accountId==0 守衛（本頁恆不觸發，D11）＋標題 新增證書/修改證書＋回填；
//     save（:119-158）POST saveCertificationAjax→close（:145）＋info（:146）＋showList（:147）；
//     del（:159-190）sysConfirm 是否確定要刪除?；close（:112-117）`$('#dialogModal').modal("show")`
//     ＝本頁無 #dialogModal 的 no-op quirk（照實＝僅關子 modal，D11）。
//   - 證照 modal（profileMaintenanceTab_certification_modal.jsp:14-86）：type*（6 選項，raw DOM
//     :1875）/name*（maxlength50）/regisDate/expireDate（datepicker→文字輸入，批次 11/13 先例）/
//     certificationFront/certificationBack（file＋預覽，photoXxxFileId≠''→顯示）/memo（maxlength30）；
//     footer 關閉/存檔；required 以 invalid 樣式鏡像 jquery validate（B1-2 typeInvalid/nameInvalid 先例）。
//
// 雛形策略（執行文件 D 系列）：存檔＝dataSource.update（endpoint-map 既有
// update:profile-profile-maintenance→/profile/saveMyAccountAjax）＋dataEffect（C5-j）；成功訊息
// 修改個人資料成功!＝ProfileController.java:118 字面值（批次 9 inline 字面值先例）；密碼不符閘＝
// 保留 inline 紅字但不阻擋存檔（D4 行為差異：C5-h′ 凍結分母要求 loginPin/pin_again 進 payload，
// 照 legacy 阻擋＝接線率紅）；證照清單存列上 certifications（B1-2 存列上鏡像；getCertificationListAjax
// 無對應 mock 資源、endpoint-map 紅線不可擴→0 data-effect 誠實標註）；照片/位置圖/證照預覽
// placeholder（getPhoto/getLocationImage/download 無真檔案服務）【雛形值待實機校準】；
// 身分欄預填 PROFILE_IDENTITY_SEED（批次 15 查詢欄預填先例）、可維護欄不預填（FC-3 值政策）。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useEffect, useState, type ReactElement } from 'react';
import { panelHeadingLevelFor } from '@/app/panel-heading-levels';
import { Button } from '@/components/base/Button';
import { PasswordInput } from '@/components/base/PasswordInput';
import { Select } from '@/components/base/Select';
import { Table, type TableRow } from '@/components/base/Table';
import { TextInput } from '@/components/base/TextInput';
import { FIELD_BASE_CLASS } from '@/components/base/shared-classes';
import { ContentPanel, ModalFrame, ProcessingOverlay, useDialogs } from '@/components/layouts';
import { LegacyContractModals } from '@/components/layouts/LegacyContractModals';
import { t } from '@/messages/zh-TW';
import {
  initialValues,
  legacyContractFieldsFor,
  MockDataError,
  toPayload,
  useDataSource,
} from '@/mocks';
import { selectOptionsOverride } from '@/mocks/legacy-fields';
import {
  ACCOUNT_OFFICE_LOCATION_OPTIONS,
  PROFILE_CERTIFICATE_TYPE_OPTIONS,
  PROFILE_IDENTITY_SEED,
} from '@/mocks/resource-rules';
import type { Row } from '@/mocks/types';
import { toUiKey } from '@/pages/templates/shared';

/** 證照 modal 表單欄位（legacy certificationForm；required 以 invalid 樣式鏡像）。 */
interface CertFormState {
  id: string;
  type: string;
  name: string;
  regisDate: string;
  expireDate: string;
  memo: string;
  typeInvalid: boolean;
  nameInvalid: boolean;
}

/** 本頁「 存檔」成功訊息（ProfileController.java:118 字面值；!＝半形 0021）。 */
const SAVE_SUCCESS_TEXT = '修改個人資料成功!';

/** 證照存檔成功訊息（CertificationController.java:88/:103）。 */
const CERT_SAVE_SUCCESS_NEW = '新增證書成功!';
const CERT_SAVE_SUCCESS_EDIT = '修改證書成功!';
/** 證照刪除成功訊息（CertificationController.java:143）。 */
const CERT_DELETE_SUCCESS_TEXT = '刪除成功!';

export function ProfileMaintenanceBody({ pageId }: { pageId: string }): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();
  const contractFields = legacyContractFieldsFor(pageId) ?? [];
  // 批次 52 修訂（b52-calibrate3，F-2）：OPTION_SET_OVERRIDES 命中＝凍結選項集取代寫死常數（規格 §7.1）。
  const overrideOptions = (name: string) => selectOptionsOverride(pageId, name);

  // ------------------------------------------------------------------
  // 資料載入（D2）：單筆列＝list rows[0]（ComplexFormPage 同款，僅作 update 目標）；
  // 證照清單＝進場即載（legacy :21 觸發 tab click→showList 鏡像）。
  // ------------------------------------------------------------------
  const [record, setRecord] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);
  const [certs, setCerts] = useState<readonly Row[]>([]);

  /** 證照清單載入（certificationTab.showList :42-64 鏡像；讀列上 certifications）。 */
  const loadCerts = () => {
    void (async () => {
      try {
        const result = await dataSource.list(pageId, { page: 1, pageSize: 1 });
        const row = result.rows[0] ?? null;
        const raw = row?.['certifications'];
        setCerts(Array.isArray(raw) ? (raw as readonly Row[]) : []);
      } catch {
        // 讀取失敗維持原清單（legacy 失敗路徑＝showAlertDialog(desc)，雛形無 desc 來源）。
      }
    })();
  };

  useEffect(() => {
    void (async () => {
      try {
        const result = await dataSource.list(pageId, { page: 1, pageSize: 1 });
        setRecord(result.rows[0] ?? null);
      } catch {
        setRecord(null); // 錯誤 scenario：無列可存（存檔走 generic 錯誤訊息）。
      } finally {
        setLoading(false);
      }
    })();
    loadCerts(); // 進場即載（legacy :20-21 ready 觸發 tab click）
    // pageId 在一次掛載內不變（ComplexFormPage 同款註記）。
    // biome-ignore lint/correctness/useExhaustiveDependencies: 見上
  }, [pageId]);

  // ------------------------------------------------------------------
  // 表單值（D6）：身分 readonly 欄預填種子（批次 15 先例）；可維護欄不預填（FC-3）；
  // eatingHabits/locationId 初始 ''（C5-h：未驅動時 payload 帶 seed，不可預填）。
  // ------------------------------------------------------------------
  const [values, setValues] = useState<Record<string, string>>(() => ({
    ...initialValues(contractFields, pageId),
    organizationName: String(PROFILE_IDENTITY_SEED['organizationName'] ?? ''),
    departmentName: String(PROFILE_IDENTITY_SEED['departmentName'] ?? ''),
    name: String(PROFILE_IDENTITY_SEED['name'] ?? ''),
    loginName: String(PROFILE_IDENTITY_SEED['loginName'] ?? ''),
    email1: String(PROFILE_IDENTITY_SEED['email1'] ?? ''),
    officeMobile: String(PROFILE_IDENTITY_SEED['officeMobile'] ?? ''),
    officeCode: String(PROFILE_IDENTITY_SEED['officeCode'] ?? ''),
    appCode: String(PROFILE_IDENTITY_SEED['appCode'] ?? ''),
    officeTel: String(PROFILE_IDENTITY_SEED['officeTel'] ?? ''),
    extNo: String(PROFILE_IDENTITY_SEED['extNo'] ?? ''),
  }));
  // 員工編號/學號UGY：legacy 無 name（:136/:142）→不進 FormData、不進 C5-h 分母，僅展示。
  const [employeeId] = useState(() => String(PROFILE_IDENTITY_SEED['employeeId'] ?? ''));
  const [schoolId] = useState(() => String(PROFILE_IDENTITY_SEED['schoolId'] ?? ''));

  const setValue = (key: string) => (next: string) => {
    setValues((current) => ({ ...current, [key]: next }));
  };

  /** locationImgDiv 顯隱（isLocationImgDiv :34-39：locationId==0→hide，其餘 show；初始 ''→show）。
   *  批次 52 修訂（b52-calibrate3）：由條件渲染（卸載）改為 hidden class 常掛——legacy 以 style
   *  藏、凍結 DOM 樹節點常在；條件卸載會讓 C5-h drive 級聯 detach（locationId 凍結選項集
   *  僅 [無(0)]，drive 必選 '0'→locationPhotoFile 離場）。視覺行為不變（==0 時照樣隱藏）。 */
  const locationImgVisible = values['locationId'] !== '0';
  const locationImgHiddenClass = locationImgVisible ? '' : ' hidden';

  // ------------------------------------------------------------------
  // 存檔（save() :42-96 鏡像；D4 行為差異見檔頭）
  // ------------------------------------------------------------------
  const [pinMismatch, setPinMismatch] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = () => {
    void (async () => {
      // :50-57 密碼不符檢核：inline 紅字（#CD5C5C 語意→text-danger）；D4：雛形不阻擋存檔。
      setPinMismatch(values['pin_again'] !== values['loginPin']);
      if (record === null) {
        await dialogs.alert(t('error.generic'));
        return;
      }
      setBusy(true);
      try {
        // C5-h：toPayload 帶齊 21 鍵（hidden id 一律帶 seed '5'；readonly 欄照 FormData 慣例帶值）。
        await dialogs.processing.wrap(
          dataSource.update(pageId, String(record.id), toPayload(values, contractFields, pageId)),
        );
        // :71 showInfoDialog(response.desc)；desc＝ProfileController.java:118 字面值。
        await dialogs.info(SAVE_SUCCESS_TEXT);
        // :76-84 照片刷新（getPhoto/getLocationImage）＝placeholder no-op【雛形值待實機校準】。
      } catch (cause: unknown) {
        // 失敗路徑＝showAlertDialog（:83）；雛形 desc 來源＝mock 錯誤訊息（B1-2 alertOrGeneric 先例）。
        await dialogs.alert(cause instanceof MockDataError ? cause.message : t('error.generic'));
      } finally {
        setBusy(false);
      }
    })();
  };

  // ------------------------------------------------------------------
  // 證照 modal（certificationTab.showEdit :66-110；D11：守衛恆不觸發不鏡像）
  // ------------------------------------------------------------------
  const [certModal, setCertModal] = useState<CertFormState | null>(null);

  /** 新增證書（showEdit(null)：:79-82）。 */
  const openNewCertification = () => {
    setCertModal({
      id: '0', // :81
      type: '',
      name: '',
      regisDate: '',
      expireDate: '',
      memo: '',
      typeInvalid: false,
      nameInvalid: false,
    });
  };

  /** 名稱連結（showEdit(rowObj)：:83-108 回填）。 */
  const showCertification = (cert: Row) => {
    setCertModal({
      id: String(cert['id'] ?? ''),
      type: String(cert['type'] ?? ''),
      name: String(cert['name'] ?? ''),
      regisDate: String(cert['regisDateString'] ?? ''),
      expireDate: String(cert['expireDateString'] ?? ''),
      memo: String(cert['memo'] ?? ''),
      typeInvalid: false,
      nameInvalid: false,
    });
  };

  /** 證照存檔（certificationTab.save :119-158；required 以 invalid 樣式鏡像 :120-122）。 */
  const saveCertification = () => {
    void (async () => {
      if (certModal === null) {
        return;
      }
      const typeInvalid = certModal.type.trim() === '';
      const nameInvalid = certModal.name.trim() === '';
      setCertModal({ ...certModal, typeInvalid, nameInvalid });
      if (typeInvalid || nameInvalid) {
        return; // $("#certificationForm").valid() 不過就不送（:120-122）
      }
      const base: Row = {
        type: certModal.type,
        name: certModal.name,
        regisDateString: certModal.regisDate,
        expireDateString: certModal.expireDate,
        memo: certModal.memo,
        erasable: 1, // CertificationController.java:78 Erasable.YES（新資料可刪）
      };
      const next: readonly Row[] =
        certModal.id === '0'
          ? [...certs, { id: `cert-${certs.length + 1}`, sort: certs.length + 1, ...base }]
          : certs.map((cert) => (String(cert['id']) === certModal.id ? { ...cert, ...base } : cert));
      if (record === null) {
        await dialogs.alert(t('error.generic'));
        return;
      }
      setBusy(true);
      try {
        // :135 POST saveCertificationAjax；雛形存列上（update 既有 op，B1-2 先例）。
        await dialogs.processing.wrap(
          dataSource.update(pageId, String(record.id), { certifications: next }),
        );
        setCertModal(null); // certificationSettingModalClose（:145；:113 dialogModal show＝no-op quirk）
        setCerts(next); // showList 鏡像（:147）
        await dialogs.info(certModal.id === '0' ? CERT_SAVE_SUCCESS_NEW : CERT_SAVE_SUCCESS_EDIT); // :146
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof MockDataError ? cause.message : t('error.generic'));
      } finally {
        setBusy(false);
      }
    })();
  };

  /** 證照刪除（certificationTab.del :159-190）。 */
  const deleteCertification = (certId: string) => {
    void (async () => {
      const confirmed = await dialogs.confirm('是否確定要刪除?'); // :160（003F）
      if (!confirmed) {
        return;
      }
      const next = certs.filter((cert) => String(cert['id']) !== certId);
      if (record === null) {
        return;
      }
      setBusy(true);
      try {
        // :166 POST deleteCertificationAjax（legacy 軟刪除＝setIsEnable(DISABLE)；雛形＝列上移除）。
        await dialogs.processing.wrap(
          dataSource.update(pageId, String(record.id), { certifications: next }),
        );
        setCerts(next);
        await dialogs.info(CERT_DELETE_SUCCESS_TEXT); // CertificationController.java:143
      } catch (cause: unknown) {
        await dialogs.alert(cause instanceof MockDataError ? cause.message : t('error.generic'));
      } finally {
        setBusy(false);
      }
    })();
  };

  // ------------------------------------------------------------------
  // 畫面 helper
  // ------------------------------------------------------------------
  // 批次 50（工作項 4）：key（payload key 語意）入 uiId/htmlFor 的字尾一律 toUiKey kebab 化
  // （§10.3 規則 1；key 參數本身不動＝name={key}/payload key/values[key] 域零影響）。
  const fieldLabel = (key: string, text: string, required = false) => (
    <label className="w-44 shrink-0 text-right text-sm" htmlFor={`${pageId}-field-${toUiKey(key)}`}>
      {required ? <span className="text-danger">*</span> : null}
      {text}
    </label>
  );

  /** 一般文字欄（type=text/password 由各 helper 分工；email 見 emailInput）。 */
  const textInput = (key: string, maxLength?: number, readOnly = false) => (
    <TextInput
      uiId={`${pageId}-field-${toUiKey(key)}`}
      name={key}
      value={values[key] ?? ''}
      onChange={setValue(key)}
      {...(maxLength !== undefined ? { maxLength } : {})}
      {...(readOnly ? { readOnly: true } : {})}
    />
  );

  /** type=email 欄（TextInput 無 type prop；照 LegacyContractFields :190-196 以 FIELD_BASE_CLASS 裸 input）。 */
  const emailInput = (key: string, maxLength: number, readOnly = false) => (
    <input
      data-ui-id={`${pageId}-field-${toUiKey(key)}`}
      type="email"
      name={key}
      className={FIELD_BASE_CLASS}
      value={values[key] ?? ''}
      maxLength={maxLength}
      readOnly={readOnly}
      onChange={(event) => {
        setValue(key)(event.target.value);
      }}
    />
  );

  /** type=file 欄（LegacyContractFields :174-188 慣例：value 不掛，選檔後檔名進 state 進 payload）。 */
  const fileInput = (key: string) => (
    <input
      data-ui-id={`${pageId}-field-${toUiKey(key)}`}
      type="file"
      name={key}
      className={`${FIELD_BASE_CLASS} text-sm`}
      accept=".png, .jpg, .jpeg"
      onChange={(event) => {
        setValue(key)(event.target.files?.[0]?.name ?? event.target.value);
      }}
    />
  );

  return (
    <div data-ui-id={pageId}>
      <LegacyContractModals pageId={pageId} />
      <ProcessingOverlay uiId={`${pageId}-processing`} open={loading} />
      <ContentPanel
        uiId={`${pageId}-panel`}
        title="個人基本資料維護"
        headingLevel={panelHeadingLevelFor(pageId)}
      >
        {/* 表單（profileMaintenance.jsp:108-285 form-horizontal；label col-sm-2＋欄 col-sm-3 雙欄制→grid-cols-2）。 */}
        <div data-ui-id={`${pageId}-item-form`} className="grid gap-x-6 gap-y-2 md:grid-cols-2">
          {/* :109 hidden id（legacy FormData 帶值；payload 由 toPayload seeds 恆帶 seed '5'）。 */}
          <input
            data-ui-id={`${pageId}-field-id`}
            type="hidden"
            name="id"
            value={values['id'] ?? ''}
            onChange={(event) => {
              setValue('id')(event.target.value);
            }}
          />
          <div className="flex items-center gap-2">
            {fieldLabel('organizationName', '醫院別：')}
            {textInput('organizationName', undefined, true)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('departmentName', '所屬單位：')}
            {textInput('departmentName', undefined, true)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('name', '姓名：')}
            {textInput('name', undefined, true)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('loginName', '登入帳號：')}
            {textInput('loginName', undefined, true)}
          </div>
          <div className="flex items-center gap-2">
            {/* :134-144 員工編號/學號UGY：無 name（不進 FormData）；唯展示。 */}
            <label className="w-44 shrink-0 text-right text-sm" htmlFor={`${pageId}-field-employee-id`}>
              員工編號：
            </label>
            <TextInput
              uiId={`${pageId}-field-employee-id`}
              value={employeeId}
              onChange={() => undefined}
              readOnly
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-44 shrink-0 text-right text-sm" htmlFor={`${pageId}-field-school-id`}>
              學號(UGY)：
            </label>
            <TextInput
              uiId={`${pageId}-field-school-id`}
              value={schoolId}
              onChange={() => undefined}
              readOnly
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('email1', '電子信箱(公務)：')}
            {emailInput('email1', 45, true)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('officeMobile', '行動電話(公務)：')}
            {textInput('officeMobile', 25, true)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('officeCode', '簡碼：')}
            {textInput('officeCode', 25, true)}
          </div>
          <div className="flex items-center gap-2">
            {/* :165-171 App簡碼 name=appCode（id=officeMobile 重覆 quirk 不重現，D11）。 */}
            {fieldLabel('appCode', 'App簡碼：')}
            {textInput('appCode', 25, true)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('officeTel', '工作地點號碼：')}
            {textInput('officeTel', 25, true)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('extNo', '工作地點分機：')}
            {textInput('extNo', 25, true)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('photoFile', '照片上傳：')}
            {fileInput('photoFile')}
          </div>
          {/* :189-190 照片建議（col-sm-7 label）。 */}
          <div className="flex items-center text-sm text-ink md:col-span-1">
            (照片尺寸建議為 400*400, 檔案大小不超過2MB)
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('loginPin', '本機密碼：')}
            <PasswordInput
              uiId={`${pageId}-field-login-pin`}
              name="loginPin"
              value={values['loginPin'] ?? ''}
              onChange={setValue('loginPin')}
            />
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('pin_again', '確認本機密碼：')}
            <div>
              <PasswordInput
                uiId={`${pageId}-field-pin-again`}
                name="pin_again"
                value={values['pin_again'] ?? ''}
                onChange={setValue('pin_again')}
              />
              {/* :52 密碼不符合 inline 紅字（insertAfter pin_again 鏡像；D4：不阻擋存檔）。 */}
              {pinMismatch ? (
                <p data-ui-id={`${pageId}-pin-error`} className="text-sm text-[#CD5C5C]">
                  密碼不符合!
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2 md:col-span-2">
            {fieldLabel('address', '通訊地址：')}
            {textInput('address', 45)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('phCode', '個人市話號碼：')}
            {textInput('phCode', 25)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('mobile', '行動電話(私人)：')}
            {textInput('mobile', 25)}
          </div>
          <div className="flex items-center gap-2">
            {fieldLabel('email2', '電子信箱(私人)：')}
            {emailInput('email2', 45)}
          </div>
          <div className="flex items-center gap-2">
            {/* :231-244 飲食習慣（請選擇 disabled selected hidden→placeholder；葷/素字面值）。 */}
            {fieldLabel('eatingHabits', '飲食習慣：')}
            <Select
              uiId={`${pageId}-field-eating-habits`}
              name="eatingHabits"
              value={values['eatingHabits'] ?? ''}
              onChange={setValue('eatingHabits')}
              placeholder="請選擇"
              options={[
                { value: '葷', label: '葷' },
                { value: '素', label: '素' },
              ]}
            />
          </div>
          <div className="flex items-center gap-2">
            {/* :245-261 辦公室位置（value=0＝無；location 迴圈→種子池【雛形值待實機校準】）。 */}
            {fieldLabel('locationId', '辦公室位置：')}
            <Select
              uiId={`${pageId}-field-location-id`}
              name="locationId"
              value={values['locationId'] ?? ''}
              onChange={setValue('locationId')}
              options={overrideOptions('locationId') ?? [{ value: '0', label: '無' }, ...ACCOUNT_OFFICE_LOCATION_OPTIONS]}
            />
          </div>
          {/* :262-285 locationImgDiv 三列（isLocationImgDiv：locationId==0→hide，D6；
              批次 52 修訂＝hidden class 常掛，見 locationImgVisible 註解）。 */}
          <div
            data-ui-id={`${pageId}-location-img-row`}
            className={`flex items-center gap-2 md:col-span-2${locationImgHiddenClass}`}
          >
            {fieldLabel('locationPhotoFile', '辦公室位置照片：')}
            {fileInput('locationPhotoFile')}
          </div>
          <div
            data-ui-id={`${pageId}-location-img-hint`}
            className={`flex items-center text-sm text-ink md:col-span-2${locationImgHiddenClass}`}
          >
            (照片尺寸建議為 400*400, 檔案大小不超過2MB)
          </div>
          <div
            data-ui-id={`${pageId}-location-img-preview`}
            className={`flex h-[250px] w-[250px] items-center justify-center border border-dashed border-gray-400 text-xs text-gray-500${locationImgHiddenClass}`}
          >
            位置圖預覽
            <br />
            （getLocationImage；【雛形值待實機校準】）
          </div>
        </div>

        {/* :286-290 存檔鈕（btn-add onclick=save()；C5-h 送出自報＝complexBodySubmitId）。 */}
        <div className="mt-4">
          <Button
            uiId={`${pageId}-actions-save`}
            variant="primary"
            size="sm"
            type="button"
            disabled={busy}
            // C5-j：存檔真的呼叫 dataSource.update。
            dataEffect={`update:${pageId}`}
            onClick={save}
          >
            存檔
          </Button>
        </div>

        {/* :291-303 tabs-container 單 tab 證照（進場即載＝legacy :21 trigger click 鏡像）。 */}
        <div data-ui-id={`${pageId}-tabs`} className="mt-6 border-b border-gray-300">
          <button
            type="button"
            data-ui-id={`${pageId}-cert-tab`}
            className="-mb-px border border-b-0 border-gray-300 px-3 py-1 text-sm font-medium"
            onClick={loadCerts}
          >
            {/* :293 onclick=certificationTab.showList() */}
            證照
          </button>
        </div>

        {/* :297 tab-certification pane（profileMaintenanceTab_certification.jsp:192-204）。 */}
        <div data-ui-id={`${pageId}-cert-panel`} className="space-y-2 pt-3">
          <div>
            <Button
              uiId={`${pageId}-cert-new`}
              variant="primary"
              size="sm"
              type="button"
              onClick={openNewCertification}
            >
              {/* :194（D11：accountId==0 守衛在 profile 頁恆不觸發） */}
              新增證書
            </Button>
          </div>
          <Table
            uiId={`${pageId}-cert-table`}
            columns={[
              { key: 'action', header: '', align: 'center' }, // :198（width 100px 刪除鈕欄）
              { key: 'name', header: '證照名稱', align: 'center' }, // :199
              { key: 'regis', header: '登記日期/發照日期', align: 'center' }, // :200
              { key: 'expire', header: '有效日期', align: 'center' }, // :201
              { key: 'type', header: '其他', align: 'center' }, // :202（資料=type，D11 照實）
            ]}
            rows={certs as readonly TableRow[]}
            rowKey={(cert) => String(cert['id'])}
            emptyText="查無符合的資料"
            renderCell={(cert, columnKey) => {
              if (columnKey === 'action') {
                // :4-18 erasable==1 才有刪除。
                return String(cert['erasable'] ?? '') === '1' ? (
                  <Button
                    uiId={`${pageId}-cert-delete-${String(cert['id'])}`}
                    variant="danger"
                    size="sm"
                    type="button"
                    // C5-j：刪除真的 update certifications。
                    dataEffect={`update:${pageId}`}
                    onClick={() => {
                      deleteCertification(String(cert['id']));
                    }}
                  >
                    刪除
                  </Button>
                ) : (
                  '\u00a0'
                );
              }
              if (columnKey === 'name') {
                // :15 `<a class="showEdit">` 名稱連結。
                return (
                  <button
                    data-ui-id={`${pageId}-cert-edit-${String(cert['id'])}`}
                    type="button"
                    className="underline"
                    onClick={() => {
                      showCertification(cert);
                    }}
                  >
                    {String(cert['name'] ?? '')}
                  </button>
                );
              }
              if (columnKey === 'regis') {
                return String(cert['regisDateString'] ?? '');
              }
              if (columnKey === 'expire') {
                return String(cert['expireDateString'] ?? '');
              }
              if (columnKey === 'type') {
                // :26-27 col4 資料='type'（表頭「其他」＝legacy 命名 quirk 照實）。
                return String(cert['type'] ?? '');
              }
              return '\u00a0';
            }}
          />
        </div>
      </ContentPanel>

      {/* 證照 modal（profileMaintenanceTab_certification_modal.jsp:14-86；疊放自訂 modal，B1-2 同款）。 */}
      {certModal !== null ? (
        <ModalFrame
          uiId={`${pageId}-cert-modal`}
          open
          title={certModal.id === '0' ? '新增證書' : '修改證書'} // :80/:83
          size="lg"
          onClose={() => {
            setCertModal(null); // certificationSettingModalClose（:112-117；dialogModal show＝no-op quirk）
          }}
          footer={
            <>
              <Button
                uiId={`${pageId}-cert-modal-footer-close`}
                variant="default"
                size="sm"
                type="button"
                onClick={() => {
                  setCertModal(null);
                }}
              >
                {/* :84 */}
                關閉
              </Button>
              <Button
                uiId={`${pageId}-cert-modal-save`}
                variant="primary"
                size="sm"
                type="button"
                disabled={busy}
                dataEffect={`update:${pageId}`}
                onClick={saveCertification}
              >
                {/* :85 */}
                存檔
              </Button>
            </>
          }
        >
          <div data-ui-id={`${pageId}-cert-modal-body`} className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="w-44 shrink-0 text-sm" htmlFor={`${pageId}-cert-field-type`}>
                <span className="text-danger">*</span>證照類別：{/* :28 */}
              </label>
              <Select
                uiId={`${pageId}-cert-field-type`}
                value={certModal.type}
                invalid={certModal.typeInvalid}
                onChange={(next) => {
                  setCertModal({ ...certModal, type: next, typeInvalid: false });
                }}
                placeholder="請選擇"
                options={PROFILE_CERTIFICATE_TYPE_OPTIONS}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-44 shrink-0 text-sm" htmlFor={`${pageId}-cert-field-name`}>
                <span className="text-danger">*</span>證照名稱：{/* :37 */}
              </label>
              <TextInput
                uiId={`${pageId}-cert-field-name`}
                value={certModal.name}
                invalid={certModal.nameInvalid}
                maxLength={50}
                onChange={(next) => {
                  setCertModal({ ...certModal, name: next, nameInvalid: false });
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-44 shrink-0 text-sm" htmlFor={`${pageId}-cert-field-regis-date`}>
                登記或發照日期：{/* :45 */}
              </label>
              {/* :48-51 datepicker；雛形簡化為文字輸入【雛形值待實機校準】 */}
              <TextInput
                uiId={`${pageId}-cert-field-regis-date`}
                value={certModal.regisDate}
                maxLength={10}
                onChange={(next) => {
                  setCertModal({ ...certModal, regisDate: next });
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="w-44 shrink-0 text-sm" htmlFor={`${pageId}-cert-field-expire-date`}>
                有效日期：{/* :54 */}
              </label>
              {/* :57-60 datepicker；雛形簡化為文字輸入【雛形值待實機校準】 */}
              <TextInput
                uiId={`${pageId}-cert-field-expire-date`}
                value={certModal.expireDate}
                maxLength={10}
                onChange={(next) => {
                  setCertModal({ ...certModal, expireDate: next });
                }}
              />
            </div>
            <div className="flex items-start gap-2">
              <label className="w-44 shrink-0 text-sm" htmlFor={`${pageId}-cert-field-certification-front`}>
                證照上傳(正)：
              </label>
              <input
                data-ui-id={`${pageId}-cert-field-certification-front`}
                type="file"
                accept=".png, .jpg, .jpeg"
                className="text-sm"
              />
              {/* :60-63 預覽（photoFrontFileId≠''→顯示；download 無真檔案服務→placeholder）。 */}
              {certModal.id !== '0' ? (
                <div
                  data-ui-id={`${pageId}-cert-front-preview`}
                  className="flex h-[120px] w-[120px] items-center justify-center border border-dashed border-gray-400 text-center text-xs text-gray-500"
                >
                  正面預覽（download）
                </div>
              ) : null}
            </div>
            <div className="flex items-start gap-2">
              <label className="w-44 shrink-0 text-sm" htmlFor={`${pageId}-cert-field-certification-back`}>
                證照上傳(反)：
              </label>
              <input
                data-ui-id={`${pageId}-cert-field-certification-back`}
                type="file"
                accept=".png, .jpg, .jpeg"
                className="text-sm"
              />
              {certModal.id !== '0' ? (
                <div
                  data-ui-id={`${pageId}-cert-back-preview`}
                  className="flex h-[120px] w-[120px] items-center justify-center border border-dashed border-gray-400 text-center text-xs text-gray-500"
                >
                  反面預覽（download）
                </div>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              <label className="w-44 shrink-0 text-sm" htmlFor={`${pageId}-cert-field-memo`}>
                備註：{/* :80 */}
              </label>
              <TextInput
                uiId={`${pageId}-cert-field-memo`}
                value={certModal.memo}
                maxLength={30}
                onChange={(next) => {
                  setCertModal({ ...certModal, memo: next });
                }}
              />
            </div>
          </div>
        </ModalFrame>
      ) : null}
    </div>
  );
}
